import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import dusiaLogo from "@/assets/dusia-final.png";

const ROTATE_MS = 6500;

type AdKind = "welcome" | "announcement";

interface AdLink {
  text: string;
  href: string;
  external?: boolean;
}

interface AdSlide {
  id: string;
  kind: AdKind;
  eyebrow: string;
  headline: string;
  sub: string;
  primary: AdLink;
  secondary?: AdLink;
  /** Render-side mockup */
  mockup: React.ReactNode;
}

/* ──────────────────────────────────────────────
   Mockup: WELCOME (logged-in) — Smiley OR Dusia logo (rotates per session)
   ────────────────────────────────────────────── */
const SmileyMockup = () => (
  <div className="relative h-full aspect-square mx-auto">
    <div className="absolute -top-4 -right-4 w-20 h-20 bg-[hsl(var(--red))] rotate-6" />
    <svg viewBox="0 0 400 400" className="relative w-full h-full" aria-hidden>
      <defs>
        <radialGradient id="hero-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="hsl(var(--yellow-glow))" stopOpacity="0.95" />
          <stop offset="60%" stopColor="hsl(var(--yellow-glow))" stopOpacity="0.4" />
          <stop offset="100%" stopColor="hsl(var(--yellow-glow))" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="200" cy="200" r="190" fill="url(#hero-glow)" />
      <circle cx="200" cy="200" r="120" fill="hsl(var(--yellow))" />
      <circle cx="170" cy="180" r="10" fill="hsl(var(--ink))" />
      <circle cx="230" cy="180" r="10" fill="hsl(var(--ink))" />
      <path d="M 165 230 Q 200 255 235 230" stroke="hsl(var(--ink))" strokeWidth="6" fill="none" strokeLinecap="round" />
      <circle cx="200" cy="60" r="6" fill="hsl(var(--ink))" />
    </svg>
  </div>
);

const DusiaLogoMockup = () => (
  <div className="relative h-full aspect-square mx-auto flex items-center justify-center">
    <div className="absolute -top-4 -right-4 w-20 h-20 bg-[hsl(var(--red))] rotate-6" />
    <img
      src={dusiaLogo}
      alt="Dusia"
      className="relative w-full h-full object-contain"
      loading="eager"
    />
  </div>
);

/** Picks Smiley or Dusia logo on each mount (new session/login/refresh). */
const DusiaMockup = () => {
  const [variant] = useState<"smiley" | "dusia">(() =>
    Math.random() < 0.5 ? "smiley" : "dusia"
  );
  return variant === "dusia" ? <DusiaLogoMockup /> : <SmileyMockup />;
};

/* ──────────────────────────────────────────────
   Mockup: THE MAP — stylized journey map card
   ────────────────────────────────────────────── */
const ZemelapisMockup = () => (
  <div className="relative h-full aspect-square mx-auto flex items-center justify-center">
    <svg viewBox="0 0 400 320" className="relative w-full h-full" aria-hidden>
      {/* Arc */}
      <path
        d="M 90 230 Q 200 70 320 240"
        stroke="hsl(var(--ink))"
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
      />
      {/* Dots: terracotta (where you are), ink (center), mustard (destination) */}
      <circle cx="140" cy="225" r="16" fill="hsl(var(--terracotta))" />
      <circle cx="205" cy="170" r="9" fill="hsl(var(--ink))" />
      <circle cx="270" cy="150" r="16" fill="hsl(var(--mustard))" />
    </svg>
  </div>
);

/* ──────────────────────────────────────────────
   Mockup: COUPLES MAP — two arcs meeting
   ────────────────────────────────────────────── */
const PorosMockup = () => (
  <div className="relative h-full aspect-square mx-auto flex items-center justify-center">
    <div className="absolute -top-6 -left-6 w-20 h-20 rounded-full bg-[hsl(var(--yellow))]" />
    <svg viewBox="0 0 400 320" className="relative w-full h-full" aria-hidden>
      <path d="M 60 250 Q 150 60 200 170" stroke="hsl(var(--ink))" strokeWidth="6" fill="none" strokeLinecap="round" />
      <path d="M 340 250 Q 250 60 200 170" stroke="hsl(var(--terracotta))" strokeWidth="6" fill="none" strokeLinecap="round" />
      <circle cx="60" cy="250" r="16" fill="hsl(var(--terracotta))" />
      <circle cx="340" cy="250" r="16" fill="hsl(var(--mustard))" />
      <circle cx="200" cy="170" r="22" fill="hsl(var(--ink))" />
    </svg>
  </div>
);

/* ──────────────────────────────────────────────
   Mockup: ARCHETYPE — eight-point star / compass
   ────────────────────────────────────────────── */
const ArchetipasMockup = () => (
  <div className="relative h-full aspect-square mx-auto flex items-center justify-center">
    <div className="absolute -bottom-5 -right-5 w-20 h-20 bg-[hsl(var(--red))] rotate-12" />
    <svg viewBox="0 0 400 400" className="relative w-full h-full" aria-hidden>
      <circle cx="200" cy="200" r="150" fill="hsl(var(--yellow))" />
      <circle cx="200" cy="200" r="150" fill="none" stroke="hsl(var(--ink))" strokeWidth="4" />
      <path d="M200 60 L232 168 L340 200 L232 232 L200 340 L168 232 L60 200 L168 168 Z" fill="hsl(var(--ink))" />
      <circle cx="200" cy="200" r="26" fill="hsl(var(--cream))" />
      <circle cx="200" cy="200" r="10" fill="hsl(var(--terracotta))" />
    </svg>
  </div>
);


/* ──────────────────────────────────────────────
   Slide content
   ────────────────────────────────────────────── */
const renderCta = (link: AdLink, variant: "primary" | "secondary") => {
  const isPrimary = variant === "primary";
  const className = isPrimary
    ? "h-12 px-7 rounded-full text-sm font-semibold bg-ink text-cream hover:bg-ink/85 transition-transform hover:scale-[1.02]"
    : "h-12 px-7 rounded-full text-sm font-semibold border-ink/25 text-ink bg-transparent hover:bg-ink/5 transition-transform hover:scale-[1.02]";
  const inner = (
    <>
      {link.text}
      {isPrimary && <ArrowRight size={16} />}
    </>
  );
  return (
    <Button asChild variant={isPrimary ? "default" : "outline"} className={className}>
      {link.external ? (
        <a href={link.href} target="_blank" rel="noopener noreferrer">{inner}</a>
      ) : (
        <Link to={link.href}>{inner}</Link>
      )}
    </Button>
  );
};

const SlideContent = ({ slide }: { slide: AdSlide }) => (
  <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 items-center">
    {/* Mobile: visual on top — fixed height so all slides align */}
    <div className="md:hidden h-[400px] flex items-center justify-center">
      {slide.mockup}
    </div>

    <div className="md:col-span-7 max-w-2xl">
      <div className="inline-flex items-center gap-2 mb-5">
        <span className="h-1 w-6 bg-[hsl(var(--red))]" />
        <span className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--ink))]/60 font-bold">
          {slide.eyebrow}
        </span>
      </div>
      <h1 className="font-marquee text-ink text-[clamp(2.25rem,6.5vw,4.75rem)] leading-[1.02] tracking-tight font-bold mb-5">
        {slide.headline}
      </h1>
      <p className="text-ink/75 text-lg leading-relaxed mb-8 max-w-xl">
        {slide.sub}
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        {renderCta(slide.primary, "primary")}
        {slide.secondary && renderCta(slide.secondary, "secondary")}
      </div>
    </div>

    {/* Desktop: visual on right — fixed height so all slides align */}
    <div className="hidden md:flex md:col-span-5 h-[480px] items-center justify-end">
      {slide.mockup}
    </div>
  </div>
);

/* ──────────────────────────────────────────────
   Hero
   ────────────────────────────────────────────── */
const HeroCampaign = () => {
  const { session } = useAuth();
  const userMeta = session?.user?.user_metadata as { full_name?: string } | undefined;
  const firstName = (userMeta?.full_name ?? "").split(" ")[0] ?? null;

  const slides = useMemo<AdSlide[]>(() => {
    const list: AdSlide[] = [];

    // 1. Welcome slide for logged-in users — always first
    if (session) {
      list.push({
        id: "welcome",
        kind: "welcome",
        eyebrow: "Welcome back",
        headline: `Good to see you, ${firstName?.trim() || "friend"}.`,
        sub: "Today is a good day to start.",
        primary: { text: "My profile", href: "/profile" },
        secondary: { text: "The Map", href: "/map" },
        mockup: <DusiaMockup />,
      });
    }

    // 2. THE MAP
    list.push({
      id: "naujiena-zemelapis",
      kind: "announcement",
      eyebrow: "New · The Map",
      headline: "Find out where you are and where you want to be.",
      sub: "For once, let yourself see your life from the outside and read the story of it.",
      primary: { text: "Go to the Map", href: "/map" },
      mockup: <ZemelapisMockup />,
    });

    // 2b. COUPLES MAP
    list.push({
      id: "naujiena-poros-zemelapis",
      kind: "announcement",
      eyebrow: "New · For couples",
      headline: "The Couples Map — one path, two people.",
      sub: "Answer separately, read it together. One report about your relationship — where you meet and where you miss each other.",
      primary: { text: "Go to the Couples Map", href: "/couples-map" },
      mockup: <PorosMockup />,
    });

    // 2c. ARCHETYPE — free test
    list.push({
      id: "naujiena-archetipas",
      kind: "announcement",
      eyebrow: "New · Free test",
      headline: "What is your archetype?",
      sub: "Twenty questions and a text about you that you will want to keep. Free.",
      primary: { text: "Start the test", href: "/archetype" },
      mockup: <ArchetipasMockup />,
    });

    return list;
  }, [session, firstName]);

  const [idx, setIdx] = useState(0);

  // Reset index if slide count changes
  useEffect(() => {
    setIdx((i) => (i >= slides.length ? 0 : i));
  }, [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [slides.length]);

  const slide = slides[idx] ?? slides[0];
  if (!slide) return null;

  return (
    <section className="relative bg-cream pt-32 pb-16 md:py-24 min-h-[560px] flex items-center overflow-hidden">
      <div className="container mx-auto px-6">
        <div key={slide.id} className="animate-in fade-in slide-in-from-bottom-2 duration-500">
          <SlideContent slide={slide} />
        </div>

        {slides.length > 1 && (
          <div className="flex justify-center md:justify-end gap-3 mt-12">
            {slides.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setIdx(i)}
                aria-label={`${s.eyebrow} — slide ${i + 1}`}
                className={cn(
                  "group flex items-center gap-2 transition-all",
                )}
              >
                <span
                  className={cn(
                    "h-2 rounded-full transition-all",
                    i === idx ? "w-10 bg-ink" : "w-2 bg-ink/25 group-hover:bg-ink/40"
                  )}
                />
                <span
                  className={cn(
                    "text-[10px] tracking-[0.18em] uppercase font-bold transition-opacity hidden sm:inline",
                    i === idx ? "text-ink opacity-100" : "text-ink/45 opacity-0 group-hover:opacity-100"
                  )}
                >
                  {s.eyebrow}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroCampaign;
